import React, { useState, useEffect } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";

const CashAccountForm = ({ 
  initialData = {}, 
  onSubmit, 
  isLoading = false, 
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    site_location_id: "",
    max_limit: "",
    min_limit: "",
    amount: "0.00",
    ...initialData
  });
  const [contacts, setContacts] = useState([{ name: "", contact: "" }]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (initialData.contacts && initialData.contacts.length > 0) {
      setContacts(initialData.contacts);
    }
    fetchLocations();
  }, [initialData]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/locations`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index][field] = value;
    setContacts(updatedContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { name: "", contact: "" }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      contacts: contacts.filter((c) => c.name.trim() && c.contact.trim()),
    };
    onSubmit(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-slate-700">
          Account Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Enter cash account name"
          required
          className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-slate-700">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Enter account description"
          className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
          rows="3"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">
            Site Location
          </label>
          <select
            value={formData.site_location_id}
            onChange={(e) => handleInputChange("site_location_id", e.target.value)}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
          >
            <option value="">Select location</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name} - {location.city}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">
            Current Amount
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) => handleInputChange("amount", e.target.value)}
            placeholder="0.00"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
            readOnly={!isEdit}
          />
          {!isEdit && (
            <p className="text-xs text-slate-500">
              Initial amount will be set to 0.00
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">
            Max Limit
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.max_limit}
            onChange={(e) => handleInputChange("max_limit", e.target.value)}
            placeholder="Optional"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">
            Min Limit
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.min_limit}
            onChange={(e) => handleInputChange("min_limit", e.target.value)}
            placeholder="Optional"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
          />
        </div>
      </div>

      {/* Contact Persons Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-slate-800">
            Contact Persons
          </h4>
          <button
            type="button"
            onClick={addContact}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add Contact</span>
          </button>
        </div>

        {contacts.map((contact, index) => (
          <div key={index} className="flex space-x-4 items-end">
            <div className="flex-1 space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Contact Name
              </label>
              <input
                type="text"
                value={contact.name}
                onChange={(e) => handleContactChange(index, "name", e.target.value)}
                placeholder="Enter contact name"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            <div className="flex-1 space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Contact Info
              </label>
              <input
                type="text"
                value={contact.contact}
                onChange={(e) => handleContactChange(index, "contact", e.target.value)}
                placeholder="Phone or email"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            {contacts.length > 1 && (
              <button
                type="button"
                onClick={() => removeContact(index)}
                className="px-3 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="flex space-x-4 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
        >
          <span>{isLoading ? "Saving..." : isEdit ? "Update Asset" : "Register Asset"}</span>
        </button>
      </div>
    </form>
  );
};

export default CashAccountForm;









